import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import useStore from "@/services/store";
import api from "@/services/api";
import { useComponentTranslation } from "@/services/i18n";

// import Job from "./job";
import SignupForm from "./signup-form";
import SigninForm from "./signin-form";
import CompleteProfileForm from "./complete-profile-form";
import Loader from "@/components/loader";

const translations = {
  en: {
    common: {
      error: {
        email_required: "Email is required",
        password_required: "Password is required",
      },
    },
    auth: {
      sign_in: {
        subtitle: "Don't have an account?",
        button: "Sign in",
        error: {
          failed: "Failed to sign in",
        },
      },
      register: {
        subtitle: "Already have an account?",
        link_text: "Sign up",
        error_failed: "Failed to create account",
      },
    },
    job_error_not_found: "Job not found",
    application_success: "Application successful",
  },
  fr: {
    common: {
      error: {
        email_required: "L'email est requis",
        password_required: "Le mot de passe est requis",
      },
    },
    auth: {
      sign_in: {
        subtitle: "Vous n'avez pas de compte ?",
        button: "Se connecter",
        error: {
          failed: "Échec de la connexion",
        },
      },
      register: {
        subtitle: "Vous avez déjà un compte ?",
        link_text: "S'inscrire",
        error_failed: "Échec de la création du compte",
      },
    },
    job_error_not_found: "Emploi non trouvé",
    application_success: "Candidature réussie",
  },
};

export default function Apply() {
  const { t } = useComponentTranslation(translations);
  const [user, setUser] = useState();
  const [mode, setMode] = useState("signup");
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const [jobpost, setJobpost] = useState(null);
  const [application, setApplication] = useState(null);

  const navigate = useNavigate();

  const jobpost_id = new URLSearchParams(window.location.search).get("jobpost_id");

  useEffect(() => {
    fetchUser();
  }, []);

  async function fetchUser() {
    try {
      const res = await api.get("/user/signin_token");
      if (!res.ok || !res.user) throw res;
      if (res.token) api.setToken(res.token);
      setUser(res.user);
      console.log("res", res);
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    fetchJob();
  }, [jobpost_id]);

  const fetchJob = async () => {
    if (!jobpost_id) return;
    try {
      const { ok: jobpostOk, data: jobpostData } = await api.get(`/post/${jobpost_id}`);
      if (!jobpostOk) return;
      setJobpost(jobpostData);
      const { ok: jobOk, data: jobData } = await api.get(`/job/public/${jobpostData.job_id}`);
      if (!jobOk) return;
      setJob(jobData);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSigninSubmit = async (values) => {
    if (!values.email) return toast.error(t("common.error.email_required"));
    if (!values.password) return toast.error(t("common.error.password_required"));
    if (!job) return toast.error(t("job_error_not_found"));
    setSubmitting(true);
    try {
      const { user, token } = await api.post("/user/signin", values);
      if (token) api.setToken(token);
      if (!user) throw new Error(t("auth.sign_in.error.failed"));

      setUser(user);

      // const applicationData = {
      //   job_id: job._id,
      //   job_title: job.title,
      //   candidate_id: user._id,
      //   candidate_name: `${user.firstname} ${user.lastname}`,
      //   candidate_type: "AUTHENTICATED",
      //   organization_id: job.organization_id,
      //   organization_name: job.organization_name,
      //   job_post_id: jobpost._id,
      //   job_post_title: jobpost.channel_name,
      //   status: "APPLIED",
      // };

      // const applicationResponse = await api.post("/application", applicationData);
      // if (applicationResponse.ok) toast.success(t("application_success"));

      // setApplication(applicationResponse.data);
      // navigate(`/candidate/application/${applicationResponse.data._id}`);
    } catch (e) {
      toast.error(e.code);
    } finally {
      setSubmitting(false);
    }
  };

  // Extract custom answers from form values
  const extractCustomAnswers = (values) => {
    const customAnswers = [];
    const customQuestions = job?.custom_questions || [];
    
    Object.entries(values).forEach(([key, value]) => {
      if (key.startsWith("custom_") && value) {
        const questionId = key.replace("custom_", "");
        const question = customQuestions.find((q) => q.id === questionId);
        if (question) {
          customAnswers.push({
            question_id: questionId,
            question: question.question,
            answer: value,
          });
        }
      }
    });
    
    return customAnswers;
  };

  const handleSignupSubmit = async (values) => {
    if (!values.email) return toast.error(t("common.error.email_required"));
    if (!values.password) return toast.error(t("common.error.password_required"));
    if (!job) return toast.error(t("job_error_not_found"));
    setSubmitting(true);
    try {
      values.organization_id = job.organization_id;
      values.organization_name = job.organization_name;
      const { user, token } = await api.post("/user/signup_candidate", values);

      if (!user || !token) throw new Error(t("auth.sign_in.error.failed"));

      api.setToken(token);
      setUser(user);

      const applicationData = {
        job_id: job._id,
        job_title: job.title,
        candidate_id: user._id,
        candidate_name: `${user.firstname} ${user.lastname}`,
        candidate_type: "AUTHENTICATED",
        organization_id: job.organization_id,
        organization_name: job.organization_name,
        candidate_has_applied: true,
        application_date: new Date().toISOString(),
        status: "APPLIED",
        job_post_id: jobpost_id,
        job_post_title: jobpost.channel_name,
        custom_answers: extractCustomAnswers(values),
      };

      const applicationResponse = await api.post("/application", applicationData);
      if (applicationResponse.ok) toast.success(t("application_success"));

      setApplication(applicationResponse.data);
      navigate(`/candidate/application/${applicationResponse.data._id}`);
    } catch (e) {
      console.error(e);
      toast.error(e.code);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteProfileSubmit = async (values) => {
    if (!values.email) return toast.error(t("common.error.email_required"));
    if (!job) return toast.error(t("job_error_not_found"));
    if (!user) return toast.error("User not found");
    setSubmitting(true);
    try {
      // Update user profile with missing information
      await api.put(`/user/${user._id}`, values);

      const applicationData = {
        job_id: job._id,
        job_title: job.title,
        candidate_id: user._id,
        candidate_name: `${values.firstname} ${values.lastname}`,
        candidate_type: "AUTHENTICATED",
        organization_id: job.organization_id,
        organization_name: job.organization_name,
        candidate_has_applied: true,
        application_date: new Date().toISOString(),
        status: "APPLIED",
        job_post_id: jobpost_id,
        job_post_title: jobpost.channel_name,
        custom_answers: extractCustomAnswers(values),
      };

      const applicationResponse = await api.post("/application", applicationData);
      if (applicationResponse.ok) toast.success(t("application_success"));

      navigate(`/candidate/application/${applicationResponse.data._id}`);
      setApplication(applicationResponse.data);
    } catch (e) {
      console.error(e);
      toast.error(e.code);
    } finally {
      setSubmitting(false);
    }
  };

  // if (!jobpost_id) return <Navigate to="/" />;
  if (!job) return <Loader />;
  if (user && application) {
    return <Navigate to={`/candidate/application/${application._id}`} />;
  }
  // if (user) return <Navigate to="/" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w- mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* <div className="grid grid-cols-1 lg:grid-cols-5 gap-8"> */}
        {/* Left Column - Job Description */}
        {/* <div className="order-2 lg:order-1 col-span-3">
            <Job job={job} />
          </div> */}

        {/* Right Column - Application Form (Sticky) */}
        <div className="order-1 lg:order-2 col-span-2 max-w-xl mx-auto">
          <div className="sticky top-8">
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              {/* Job Title Section */}
              {job && job.title && (
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 font-medium">You're applying to</p>
                      <h2 className="text-xl font-bold text-gray-900 mt-0.5">{job.title}</h2>
                    </div>
                  </div>
                </div>
              )}

              {user ? (
                <CompleteProfileForm user={user} onSubmit={handleCompleteProfileSubmit} loading={submitting} job={job} />
              ) : mode === "signin" ? (
                <SigninForm onSubmit={handleSigninSubmit} loading={submitting} job={job} />
              ) : (
                <SignupForm user={user} onSubmit={handleSignupSubmit} loading={submitting} job={job} />
              )}
              {!user && (
                <p className="text-gray-600 text-sm text-center mt-4">
                  {mode === "signin" ? t("auth.sign_in.subtitle") : t("auth.register.subtitle")}{" "}
                  <button onClick={() => setMode(mode === "signin" ? "signup" : "signin")} className="text-primary font-semibold hover:underline">
                    {mode === "signin" ? t("auth.register.link_text") : t("auth.sign_in.button")}
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
        {/* </div> */}
      </div>
    </div>
  );
}
